import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import "./GoogleSignInPanel.css";

const GOOGLE_SCRIPT_ID = "google-identity-services";
const GOOGLE_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

function loadGoogleScript() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Browser environment is unavailable."));
      return;
    }

    const existing = document.getElementById(GOOGLE_SCRIPT_ID);
    if (existing && window.google?.accounts?.id) {
      resolve(window.google);
      return;
    }

    if (existing) {
      existing.addEventListener("load", () => resolve(window.google), { once: true });
      existing.addEventListener("error", () => reject(new Error("Google script failed to load.")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_SCRIPT_ID;
    script.src = GOOGLE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error("Google script failed to load."));
    document.body.appendChild(script);
  });
}

const GoogleSignInPanel = () => {
  const buttonRef = useRef(null);
  const [loadError, setLoadError] = useState("");
  const {
    authMode,
    authConfigState,
    authError,
    canUseGoogleAuth,
    googleClientId,
    isAuthenticating,
    signInWithGoogleCredential,
    signInWithMock,
  } = useAuth();

  useEffect(() => {
    const buttonElement = buttonRef.current;

    if (authMode !== "google" || !canUseGoogleAuth || !googleClientId || !buttonElement) {
      return undefined;
    }

    let cancelled = false;
    buttonElement.innerHTML = "";
    setLoadError("");

    loadGoogleScript()
      .then((google) => {
        if (cancelled || !buttonElement || !google?.accounts?.id) return;

        google.accounts.id.initialize({
          client_id: googleClientId,
          callback: async (response) => {
            if (!response?.credential) return;
            try {
              await signInWithGoogleCredential(response.credential);
            } catch (error) {
              console.error("Google sign-in failed.", error);
            }
          },
        });

        google.accounts.id.renderButton(buttonElement, {
          theme: "outline",
          size: "large",
          shape: "rectangular",
          text: "signin_with",
          logo_alignment: "left",
          width: 260,
        });
      })
      .catch((error) => {
        setLoadError(error.message);
      });

    return () => {
      cancelled = true;
      if (buttonElement) {
        buttonElement.innerHTML = "";
      }
    };
  }, [authMode, canUseGoogleAuth, googleClientId, signInWithGoogleCredential]);

  if (authMode === "mock") {
    return (
      <div className="google-signin-panel">
        <button className="google-mock-button" onClick={signInWithMock}>
          ローカル確認用ログイン
        </button>
        <p className="google-signin-note">
          localhost ではモック認証で保存フローを確認できます。
        </p>
      </div>
    );
  }

  if (authMode === "disabled") {
    return (
      <div className="google-signin-panel">
        <p className="google-signin-status">Google ログインは現在準備中です。</p>
        <p className="google-signin-note">デッキ保存はログイン設定の反映後に利用できます。</p>
      </div>
    );
  }

  if (!canUseGoogleAuth) {
    return (
      <div className="google-signin-panel">
        <p className="google-signin-status">
          {authConfigState === "missing_api_base_url"
            ? "ログイン接続設定の反映待ちです。"
            : "Google ログインは現在準備中です。"}
        </p>
        <p className="google-signin-note">設定が整い次第、この画面からログインできます。</p>
      </div>
    );
  }

  return (
    <div className="google-signin-panel">
      <div ref={buttonRef} />
      {!isAuthenticating && !loadError && !authError ? (
        <p className="google-signin-note">Google ログインでデッキ保存とロードを利用できます。</p>
      ) : null}
      {isAuthenticating ? (
        <p className="google-signin-note">ログイン処理中です...</p>
      ) : null}
      {loadError || authError ? (
        <p className="google-signin-error">{loadError || authError}</p>
      ) : null}
    </div>
  );
};

export default GoogleSignInPanel;
